/* custom-phaser-battery-fit.js
 * "Will my battery fit?" Bat-Safe sizing tool.
 *
 * Answers the three things that drive most Bat-Safe support tickets (see the
 * CommsLayer product-question threads):
 *   1. will the battery physically fit,
 *   2. is it under the box's watt-hour rating,
 *   3. does it still fit once the charger is plugged in.
 *
 * That third one is what catches e-bike, scooter and golf buggy owners out.
 * The pack drops in fine, then the charge plug on the end makes it too long to
 * shut the lid. FPV packs do not have the problem, so the port step only shows
 * for the battery types where it matters.
 *
 * Box specs come from the section settings, rendered into a JSON tag by
 * Liquid, so dimensions, prices and links stay editable in the theme editor.
 * Phaser-only.
 */
(function () {
  'use strict';

  var CELL_NOMINAL = { lipo: 3.7, liion: 3.6, lifepo4: 3.2 };

  /* Battery presets. `port` decides whether the charging-plug step is shown,
   * `plug` is a sensible starting allowance in mm for that kind of charger. */
  var TYPES = {
    fpv: { chem: 'lipo', plug: 0, port: false, autoPort: false },
    ebike: { chem: 'liion', plug: 35, port: true, autoPort: true },
    buggy: { chem: 'liion', plug: 40, port: true, autoPort: true },
    tool: { chem: 'liion', plug: 0, port: true, autoPort: false }
  };

  /* Most charge sockets sit on an end, so that is the face we assume. */
  var DEFAULT_PORT_AXIS = 0;

  /* ---------- geometry ------------------------------------------------ */

  /**
   * The six ways a cuboid can sit axis-aligned, as index permutations rather
   * than swapped numbers. Keeping the indices lets the caller follow where a
   * given axis ended up, which is how the plug gets drawn on the correct face
   * after the battery has been turned on its side.
   */
  var PERMS = [
    [0, 1, 2], [0, 2, 1],
    [1, 0, 2], [1, 2, 0],
    [2, 0, 1], [2, 1, 0]
  ];

  function applyPerm(d, p) {
    return [d[p[0]], d[p[1]], d[p[2]]];
  }

  /**
   * Best orientation of `batt` inside `box`, or null if it never fits. Because
   * every permutation is tried, the order the customer types length, width and
   * height in makes no difference to the answer.
   */
  function bestOrientation(batt, box) {
    var best = null;
    PERMS.forEach(function (p) {
      var o = applyPerm(batt, p);
      if (o[0] > box.w || o[1] > box.d || o[2] > box.h) return;
      var slack = Math.min(box.w - o[0], box.d - o[1], box.h - o[2]);
      if (!best || slack > best.slack) best = { dims: o, perm: p, slack: slack };
    });
    return best;
  }

  /** Orientation that overflows least, for drawing a battery that is too big. */
  function tightestOrientation(batt, box) {
    var best = null;
    PERMS.forEach(function (p) {
      var o = applyPerm(batt, p);
      var over =
        Math.max(0, o[0] - box.w) +
        Math.max(0, o[1] - box.d) +
        Math.max(0, o[2] - box.h);
      if (!best || over < best.over) best = { dims: o, perm: p, over: over };
    });
    return best;
  }

  /** How many of this battery fit, packed in a plain grid. */
  function gridCount(batt, box) {
    var best = 0;
    PERMS.forEach(function (p) {
      var o = applyPerm(batt, p);
      var n =
        Math.floor(box.w / o[0]) *
        Math.floor(box.d / o[1]) *
        Math.floor(box.h / o[2]);
      if (n > best) best = n;
    });
    return best;
  }

  /** Battery size once the charger is plugged in. `axis` is 0 L, 1 W, 2 H. */
  function withPlug(batt, axis, plug) {
    var e = batt.slice();
    if (axis !== null && axis !== undefined && plug > 0) e[axis] += plug;
    return e;
  }

  /* ---------- shareable links ----------------------------------------- */

  /**
   * Every input maps to a short query parameter, so the page can be linked
   * with a battery already filled in. That is the point: support can send a
   * customer a link that already has their pack in it, and the customer can
   * send their numbers back without retyping them into an email.
   */
  var PARAMS = [
    ['l', '[data-bfit-dim="l"]'],
    ['w', '[data-bfit-dim="w"]'],
    ['h', '[data-bfit-dim="h"]'],
    ['v', '[data-bfit-cap="volts"]'],
    ['s', '[data-bfit-cap="cells"]'],
    ['ah', '[data-bfit-cap="ah"]'],
    ['mah', '[data-bfit-cap="mah"]'],
    ['wh', '[data-bfit-cap="wh"]'],
    ['pl', '[data-bfit-plug]'],
    ['q', '[data-bfit-qty]']
  ];

  /* ---------- SVG ----------------------------------------------------- */

  var NS = 'http://www.w3.org/2000/svg';

  function el(name, attrs) {
    var n = document.createElementNS(NS, name);
    Object.keys(attrs || {}).forEach(function (k) {
      if (attrs[k] !== null && attrs[k] !== undefined) n.setAttribute(k, attrs[k]);
    });
    return n;
  }

  function clear(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
  }

  /**
   * Cabinet projection of a cuboid. `ox, oy` is the bottom-front-left corner.
   *
   *        +--------+          top face
   *       /        /|
   *      +--------+ |          front face + right side
   *      |        | +
   *      |        |/
   *      +--------+            <- (ox, oy)
   */
  var DX = 0.42;
  var DY = 0.3;

  function faces(ox, oy, w, d, h) {
    var dx = d * DX;
    var dy = d * DY;
    var fy = oy - h;
    return {
      front: [ox, fy, ox + w, fy, ox + w, fy + h, ox, fy + h].join(','),
      top: [ox, fy, ox + dx, fy - dy, ox + w + dx, fy - dy, ox + w, fy].join(','),
      side: [ox + w, fy, ox + w + dx, fy - dy, ox + w + dx, fy + h - dy, ox + w, fy + h].join(','),
      dx: dx,
      dy: dy
    };
  }

  /**
   * Draw a solid. Each face gets its base colour from CSS (so the theme's light
   * and dark palettes both work) plus a neutral gradient overlay that does the
   * 3D shading. Keeping the shading neutral means one set of gradients serves
   * every colour state.
   */
  function cuboid(ox, oy, w, d, h, cls, opts) {
    var f = faces(ox, oy, w, d, h);
    var g = el('g', { class: cls });
    var o = opts || {};

    [
      ['top', 'bfitShadeTop'],
      ['side', 'bfitShadeSide'],
      ['front', 'bfitShadeFront']
    ].forEach(function (pair) {
      var name = pair[0];
      g.appendChild(el('polygon', { points: f[name], class: cls + '__' + name }));
      if (!o.flat) {
        g.appendChild(el('polygon', {
          points: f[name],
          fill: 'url(#' + pair[1] + ')',
          'pointer-events': 'none'
        }));
      }
    });

    return g;
  }

  /** Place a solid at an offset from an origin, in projected space. */
  function cuboidAt(ox, oy, off, dims, cls, opts) {
    var x = ox + off[0] + off[1] * DX;
    var y = oy - off[2] - off[1] * DY;
    return cuboid(x, y, dims[0], dims[1], dims[2], cls, opts);
  }

  /** Soft contact shadow so solids sit on a surface rather than float. */
  function groundShadow(ox, oy, w, d, cls) {
    var cx = ox + (w + d * DX) / 2;
    var cy = oy - (d * DY) / 2;
    return el('ellipse', {
      cx: cx,
      cy: cy,
      rx: (w + d * DX) * 0.55,
      ry: Math.max(d * DY * 0.75, 4),
      class: cls || 'bfit-shadow',
      'pointer-events': 'none'
    });
  }

  /**
   * Where the plug ends up after the battery has been rotated.
   * Returns the drawn-axis index (0 across, 1 back, 2 up) or null.
   */
  function plugDrawAxis(perm, plugAxis) {
    if (plugAxis === null || plugAxis === undefined) return null;
    return perm.indexOf(plugAxis);
  }

  /**
   * Draw a Bat-Safe interior with the battery, and its plug, inside it to scale.
   * `state` is one of: fits | over | plug-blocked | too-big | empty.
   */
  function drawFit(svg, box, ctx, state) {
    clear(svg);

    var VIEW_W = 220;
    var VIEW_H = 170;
    var PAD = 14;

    var batt = ctx.batt;
    var plugLen = ctx.plugLen;
    var plugAxis = ctx.plugAxis;

    // What has to be drawn: the battery plus its plug.
    var solid = batt ? withPlug(batt, plugAxis, plugLen) : null;

    var pick = null;
    if (solid) {
      pick = state === 'too-big' || state === 'plug-blocked'
        ? tightestOrientation(solid, box)
        : bestOrientation(solid, box);
      if (!pick) pick = tightestOrientation(solid, box);
    }

    // Scale to whatever must appear. When it does not fit that includes the
    // overflow, so you can see how far past the box it runs.
    var extW = box.w;
    var extD = box.d;
    var extH = box.h;
    if (pick && (state === 'too-big' || state === 'plug-blocked')) {
      extW = Math.max(extW, pick.dims[0]);
      extD = Math.max(extD, pick.dims[1]);
      extH = Math.max(extH, pick.dims[2]);
    }

    var s = Math.min(
      (VIEW_W - PAD * 2) / (extW + extD * DX),
      (VIEW_H - PAD * 2 - 6) / (extH + extD * DY)
    );

    var ox = (VIEW_W - (extW * s + extD * s * DX)) / 2;
    var oy = (VIEW_H + (extH * s + extD * s * DY)) / 2 - extD * s * DY - 3;

    svg.setAttribute('viewBox', '0 0 ' + VIEW_W + ' ' + VIEW_H);

    svg.appendChild(groundShadow(ox, oy, box.w * s, box.d * s));
    svg.appendChild(cuboid(ox, oy, box.w * s, box.d * s, box.h * s, 'bfit-box'));

    if (!pick) return;

    var dims = pick.dims.slice();
    var pAxis = plugDrawAxis(pick.perm, plugAxis);

    // Split the drawn solid back into body and plug. `dims` is in millimetres,
    // so the plug has to come off in millimetres too before anything is scaled.
    var body = dims.slice();
    if (pAxis !== null) body[pAxis] = Math.max(dims[pAxis] - plugLen, 1);

    var pLen = pAxis === null ? 0 : Math.min(plugLen, dims[pAxis]) * s;

    var g = el('g', { class: 'bfit-batt bfit-batt--' + state });
    g.appendChild(cuboid(ox, oy, body[0] * s, body[1] * s, body[2] * s, 'bfit-batt'));

    if (pAxis !== null && pLen > 0.5) {
      // A nub on the face the customer marked, sized to read as a plug rather
      // than as more battery.
      var other = [0, 1, 2].filter(function (i) { return i !== pAxis; });
      var nub = [0, 0, 0];
      var off = [0, 0, 0];
      nub[pAxis] = pLen;
      off[pAxis] = body[pAxis] * s;
      other.forEach(function (i) {
        nub[i] = Math.max(body[i] * s * 0.42, 5);
        off[i] = (body[i] * s - nub[i]) / 2;
      });
      g.appendChild(cuboidAt(ox, oy, off, nub, 'bfit-plug'));
    }

    svg.appendChild(g);

    // Redraw the box's leading edges over the battery so it reads as inside.
    var f = faces(ox, oy, box.w * s, box.d * s, box.h * s);
    ['front', 'top', 'side'].forEach(function (n) {
      svg.appendChild(el({ front: 'polygon', top: 'polygon', side: 'polygon' }[n], {
        points: f[n],
        class: 'bfit-box__edge'
      }));
    });
  }

  /**
   * The battery in the input panel, redrawn as you type. Its three visible
   * faces are clickable so the customer can mark where the charging port is.
   */
  function drawBattery(svg, ctx, opts) {
    clear(svg);

    var VIEW_W = 270;
    var VIEW_H = 210;
    var PAD = 48;
    svg.setAttribute('viewBox', '0 0 ' + VIEW_W + ' ' + VIEW_H);

    var batt = ctx.batt;
    var ghost = !batt;
    var L = batt ? batt[0] : 95;
    var W = batt ? batt[1] : 50;
    var H = batt ? batt[2] : 34;

    var plugLen = ctx.plugLen;
    var plugAxis = ctx.plugAxis;

    // Reserve room for the plug so the drawing does not jump when it appears.
    var eL = L + (plugAxis === 0 ? plugLen : 0);
    var eW = W + (plugAxis === 1 ? plugLen : 0);
    var eH = H + (plugAxis === 2 ? plugLen : 0);

    var s = Math.min(
      (VIEW_W - PAD * 2) / (eL + eW * DX),
      (VIEW_H - PAD * 2) / (eH + eW * DY)
    );

    var w = Math.max(L * s, 10);
    var d = Math.max(W * s, 10);
    var h = Math.max(H * s, 10);
    var ox = (VIEW_W - (eL * s + eW * s * DX)) / 2;
    var oy = (VIEW_H + (eH * s + eW * s * DY)) / 2 - eW * s * DY;

    svg.appendChild(groundShadow(ox, oy, w, d));

    var cls = ghost ? 'bfit-preview bfit-preview--ghost' : 'bfit-preview';
    svg.appendChild(cuboid(ox, oy, w, d, h, cls, { flat: ghost }));

    var f = faces(ox, oy, w, d, h);

    // Plug nub, drawn on the marked face.
    if (plugAxis !== null && plugLen > 0 && !ghost) {
      var sizes = [w, d, h];
      var other = [0, 1, 2].filter(function (i) { return i !== plugAxis; });
      var nub = [0, 0, 0];
      var off = [0, 0, 0];
      nub[plugAxis] = plugLen * s;
      off[plugAxis] = sizes[plugAxis];
      other.forEach(function (i) {
        nub[i] = Math.max(sizes[i] * 0.4, 6);
        off[i] = (sizes[i] - nub[i]) / 2;
      });
      svg.appendChild(cuboidAt(ox, oy, off, nub, 'bfit-plug'));
    }

    // Clickable faces for marking the port. Front is the width axis, top is the
    // height axis, right side is the length axis.
    if (opts && opts.pickable && !ghost) {
      [
        { name: 'front', axis: 1, label: 'the long side' },
        { name: 'top', axis: 2, label: 'the top' },
        { name: 'side', axis: 0, label: 'the end' }
      ].forEach(function (t) {
        var hit = el('polygon', {
          points: f[t.name],
          class: 'bfit-hit' + (plugAxis === t.axis ? ' is-picked' : ''),
          role: 'button',
          tabindex: '0',
          'data-bfit-face': t.axis,
          'aria-label': 'Charging port is on ' + t.label,
          'aria-pressed': plugAxis === t.axis ? 'true' : 'false'
        });
        svg.appendChild(hit);
      });
    }

    // Colour-coded measuring rails: height left, length below, depth to the side.
    svg.appendChild(el('line', {
      x1: ox - 10, y1: oy, x2: ox - 10, y2: oy - h,
      class: 'bfit-rail bfit-rail--h'
    }));
    svg.appendChild(el('text', {
      x: ox - 17, y: oy - h / 2, class: 'bfit-rail__key bfit-rail__key--h',
      'text-anchor': 'end', 'dominant-baseline': 'middle'
    })).textContent = 'H';

    svg.appendChild(el('line', {
      x1: ox, y1: oy + 10, x2: ox + w, y2: oy + 10,
      class: 'bfit-rail bfit-rail--l'
    }));
    svg.appendChild(el('text', {
      x: ox + w / 2, y: oy + 25, class: 'bfit-rail__key bfit-rail__key--l',
      'text-anchor': 'middle'
    })).textContent = 'L';

    svg.appendChild(el('line', {
      x1: ox + w + 6, y1: oy + 4, x2: ox + w + f.dx + 6, y2: oy - f.dy + 4,
      class: 'bfit-rail bfit-rail--w'
    }));
    svg.appendChild(el('text', {
      x: ox + w + f.dx + 14, y: oy - f.dy + 7, class: 'bfit-rail__key bfit-rail__key--w',
      'text-anchor': 'start'
    })).textContent = 'W';
  }

  /* ---------- formatting ---------------------------------------------- */

  function num(v) {
    if (!isFinite(v)) return '';
    return String(Math.round(v * 10) / 10);
  }

  function list(items) {
    if (items.length === 1) return items[0];
    return items.slice(0, -1).join(', ') + ' and ' + items[items.length - 1];
  }

  /** Agrees a verb with a list, so we never print "the A and B is". */
  function verb(items, one, many) {
    return items.length === 1 ? one : many;
  }

  function esc(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  /* ---------- component ----------------------------------------------- */

  function init(root) {
    if (root.dataset.bfitReady === '1') return;

    var dataTag = root.querySelector('[data-bfit-boxes]');
    if (!dataTag) return;

    var boxes;
    try {
      boxes = JSON.parse(dataTag.textContent);
    } catch (e) {
      return;
    }
    if (!boxes || !boxes.length) return;

    // Smallest first, so "the smallest one that fits" is just the first match.
    boxes.sort(function (a, b) {
      return a.w * a.d * a.h - b.w * b.d * b.h;
    });

    root.dataset.bfitReady = '1';

    var els = {
      typeBtns: root.querySelectorAll('[data-bfit-type]'),
      unit: root.querySelectorAll('[data-bfit-unit]'),
      dim: root.querySelectorAll('[data-bfit-dim]'),
      unitLabels: root.querySelectorAll('[data-bfit-unit-label]'),
      preview: root.querySelector('[data-bfit-preview]'),
      previewNote: root.querySelector('[data-bfit-preview-note]'),
      portStep: root.querySelector('[data-bfit-port-step]'),
      portHint: root.querySelector('[data-bfit-port-hint]'),
      portClear: root.querySelector('[data-bfit-port-clear]'),
      portOn: root.querySelector('[data-bfit-port-on]'),
      portBody: root.querySelector('[data-bfit-port-body]'),
      portSwitchText: root.querySelector('.bfit-switch__text'),
      plugInput: root.querySelector('[data-bfit-plug]'),
      cap: root.querySelectorAll('[data-bfit-cap]'),
      qty: root.querySelector('[data-bfit-qty]'),
      qtySteps: root.querySelectorAll('[data-bfit-qty-step]'),
      chem: root.querySelector('[data-bfit-chem]'),
      whOut: root.querySelector('[data-bfit-wh-out]'),
      whValue: root.querySelector('[data-bfit-wh-value]'),
      whHow: root.querySelector('[data-bfit-wh-how]'),
      whHint: root.querySelector('[data-bfit-wh-hint]'),
      verdict: root.querySelector('[data-bfit-verdict]'),
      cards: root.querySelector('[data-bfit-cards]'),
      headroom: root.querySelector('[data-bfit-headroom]'),
      qtyRow: root.querySelector('[data-bfit-qty-row]'),
      mixToggle: root.querySelector('[data-bfit-mix-toggle]'),
      mixPanel: root.querySelector('[data-bfit-mix-panel]'),
      mixRows: root.querySelector('[data-bfit-mix-rows]'),
      mixAdd: root.querySelector('[data-bfit-mix-add]'),
      mixTotal: root.querySelector('[data-bfit-mix-total]'),
      chargerTabs: root.querySelectorAll('[data-bfit-charger]'),
      chargerAdvice: root.querySelector('[data-bfit-charger-advice]'),
      accessories: root.querySelector('[data-bfit-accessories]'),
      miniNote: root.querySelector('[data-bfit-mini-note]'),
      share: root.querySelector('[data-bfit-share]'),
      copy: root.querySelector('[data-bfit-copy]'),
    };

    var state = { unit: 'cm', type: null, plugAxis: null, charger: 'iec', mix: [], portOn: false };

    /* --- reading the form --------------------------------------------- */

    function readDims() {
      var vals = [];
      els.dim.forEach(function (i) {
        var v = parseFloat(i.value);
        vals.push(isFinite(v) && v > 0 ? v : null);
      });
      if (!vals.length || vals.indexOf(null) !== -1) return null;
      var k = state.unit === 'cm' ? 10 : 1;
      return vals.map(function (v) { return v * k; });
    }

    function val(name) {
      var i = root.querySelector('[data-bfit-cap="' + name + '"]');
      if (!i) return null;
      var v = parseFloat(i.value);
      return isFinite(v) && v > 0 ? v : null;
    }

    function plugLen() {
      if (state.plugAxis === null) return 0;
      var v = parseFloat(els.plugInput ? els.plugInput.value : '');
      if (!isFinite(v) || v <= 0) return 0;
      return state.unit === 'cm' ? v * 10 : v;
    }

    function readQty() {
      var v = parseInt(els.qty ? els.qty.value : '1', 10);
      if (!isFinite(v) || v < 1) return 1;
      return Math.min(v, 99);
    }

    /**
     * Watt-hours from whatever the customer filled in. Every field is on screen
     * at once, so they enter the numbers printed on their battery and we do the
     * rest. Returns { wh, how } or { wh: null, missing }.
     */
    function readWh() {
      var direct = val('wh');
      if (direct) {
        return { wh: direct, how: 'straight from the watt-hours you entered' };
      }

      var notes = [];

      var voltage = null;
      var v = val('volts');
      var cells = val('cells');
      if (v) {
        voltage = v;
      } else if (cells) {
        var chem = els.chem ? els.chem.value : 'liion';
        var per = CELL_NOMINAL[chem] || 3.6;
        voltage = cells * per;
        notes.push(cells + 'S at ' + per + 'V a cell');
      }

      var amps = null;
      var ah = val('ah');
      var mah = val('mah');
      if (ah) {
        amps = ah;
      } else if (mah) {
        amps = mah / 1000;
        notes.push(num(mah) + 'mAh converted to Ah');
      }

      if (voltage && amps) {
        var how = num(voltage) + 'V times ' + num(amps) + 'Ah';
        if (notes.length) how += ' (' + notes.join(', ') + ')';
        return { wh: voltage * amps, how: how };
      }
      if (voltage) return { wh: null, missing: 'Add the Ah or mAh printed on your battery.' };
      if (amps) return { wh: null, missing: 'Add the voltage, or the cell count.' };
      return { wh: null, missing: null };
    }

    /* --- the answer ---------------------------------------------------- */

    /**
     * `qty` is how many are going in at once. Room and watt-hours are both
     * shared, so whichever runs out first is the answer.
     *
     * `mixed` changes the physical test. With a listed mix the sizes differ,
     * and the dimensions on screen are only the biggest pack, so counting
     * `qty` copies of that would wrongly rule out a drawer of mostly small
     * packs. There the honest checks are: does the biggest one go in, and is
     * the total under the rating.
     */
    function evaluate(batt, solid, wh, qty, mixed) {
      return boxes.map(function (box) {
        var fitsBare = batt ? !!bestOrientation(batt, box) : null;
        var fitsSolid = solid ? !!bestOrientation(solid, box) : null;

        // Most that physically go in, and most the rating allows.
        var roomFor = solid && fitsSolid ? gridCount(solid, box) : 0;
        var capFor = wh ? Math.floor(box.wh / wh) : Infinity;
        var holds = Math.min(roomFor, capFor);

        var st;
        if (batt === null) st = 'empty';
        else if (!fitsBare) st = 'too-big';
        else if (!fitsSolid) st = 'plug-blocked';
        else if (capFor < qty) st = 'over';
        else if (!mixed && roomFor < qty) st = 'no-room';
        else st = 'fits';

        return {
          box: box,
          state: st,
          holds: mixed ? capFor : (isFinite(holds) ? holds : roomFor),
          roomFor: roomFor,
          capFor: capFor
        };
      });
    }

    var LABEL = {
      fits: 'It fits',
      over: 'Over capacity',
      'no-room': 'Not enough room',
      'plug-blocked': 'Plug will not clear',
      'too-big': 'Too small',
      empty: 'Waiting'
    };

    function cardMarkup(r, qty) {
      var b = r.box;
      var inside = num(b.w) + ' x ' + num(b.d) + ' x ' + num(b.h) + 'mm';
      var many = qty > 1;
      var note;

      if (r.state === 'empty') {
        note = 'Enter your measurements and we will check this one.';
      } else if (r.state === 'too-big') {
        note = 'Your battery is bigger than this box on the inside.';
      } else if (r.state === 'plug-blocked') {
        note = 'The battery on its own fits. With the charger plugged in it does not.';
      } else if (r.state === 'over') {
        note = many
          ? qty + ' of them comes to more than the ' + b.wh + 'Wh this box is rated for. ' +
            (r.capFor > 0 ? 'It will take ' + r.capFor + '.' : '')
          : 'It goes in, but this box is only rated to ' + b.wh + 'Wh and yours is over that.';
      } else if (r.state === 'no-room') {
        note = 'There is not room for ' + qty + '. This one holds about ' + r.roomFor + '.';
      } else if (many) {
        note = 'All ' + qty + ' fit, and together they are under the ' + b.wh + 'Wh limit.';
      } else if (r.holds > 1) {
        note = 'Fits, and under the ' + b.wh + 'Wh limit. Room for roughly ' + r.holds + ' of them.';
      } else {
        note = 'Fits, and under the ' + b.wh + 'Wh limit.';
      }

      return '' +
        '<article class="bfit-card bfit-card--' + r.state + '">' +
          '<div class="bfit-card__head">' +
            '<h3 class="bfit-card__name">' + esc(b.name) + '</h3>' +
            '<span class="bfit-card__badge">' + LABEL[r.state] + '</span>' +
          '</div>' +
          '<div class="bfit-card__viz">' +
            '<svg class="bfit-card__svg" role="img" aria-label="' +
              esc(b.name) + ' interior, with your battery drawn to scale"></svg>' +
          '</div>' +
          '<p class="bfit-card__note">' + note + '</p>' +
          '<dl class="bfit-card__specs">' +
            '<div><dt>Inside</dt><dd>' + inside + '</dd></div>' +
            '<div><dt>Rated to</dt><dd>' + b.wh + 'Wh</dd></div>' +
          '</dl>' +
          '<a class="bfit-card__cta" href="' + esc(b.url) + '">' +
            '<span>' + (r.state === 'fits' ? 'Buy the ' : 'View the ') + esc(b.short) + '</span>' +
            '<span class="bfit-card__price">' + esc(b.price) + '</span>' +
          '</a>' +
        '</article>';
    }

    function verdictMarkup(results, batt, wh, plugOn) {
      if (batt === null) {
        return '<div class="bfit-verdict__inner bfit-verdict--idle">' +
          '<p class="bfit-verdict__head">Start with the three measurements.</p>' +
          '<p class="bfit-verdict__body">Measure your battery at its widest points, including ' +
          'any plug or wrap that sticks out. We try it every way up, so the order you type them ' +
          'in makes no difference.</p>' +
          '</div>';
      }

      var fits = results.filter(function (r) { return r.state === 'fits'; });
      var over = results.filter(function (r) { return r.state === 'over'; });
      var noRoom = results.filter(function (r) { return r.state === 'no-room'; });
      var blocked = results.filter(function (r) { return r.state === 'plug-blocked'; });
      var no = results.filter(function (r) { return r.state === 'too-big'; });
      var biggest = boxes[boxes.length - 1];

      var qty = arguments.length > 4 ? arguments[4] : 1;
      var many = qty > 1;
      var mixed = arguments.length > 5 ? arguments[5] : false;
      var spec = num(batt[0]) + ' x ' + num(batt[1]) + ' x ' + num(batt[2]) + 'mm' +
        (many ? (mixed ? ' at the biggest' : ' each') : '');
      var whTxt = wh ? ', ' + num(wh * qty) + 'Wh' + (many ? ' all up' : '') : '';
      var subject = many ? 'Your ' + qty + ' packs' : 'Your battery';
      var size = subject + ' (' + spec + whTxt + ')';
      var names = function (rs) {
        return list(rs.map(function (r) { return r.box.short; }));
      };

      var tone;
      var head;
      var body;

      if (fits.length) {
        tone = 'yes';
        head = (qty > 1 ? 'They fit. ' : 'It fits. ') +
          'The ' + fits[0].box.short + ' is the smallest one that works.';
        body = size + (many ? ' go' : ' goes') + ' in the ' + names(fits) + '. ';
        var buts = [];
        if (no.length) {
          buts.push('the ' + names(no) + ' ' + verb(no, 'is', 'are') + ' too small');
        }
        if (blocked.length) {
          buts.push('the ' + names(blocked) + ' ' + verb(blocked, 'takes', 'take') +
            ' the battery but not the charge plug');
        }
        if (noRoom.length) {
          buts.push('the ' + names(noRoom) + ' ' + verb(noRoom, 'has', 'have') +
            ' room for fewer than ' + qty);
        }
        if (over.length) {
          buts.push('the ' + names(over) + ' ' + verb(over, 'is', 'are') + ' over ' +
            verb(over, 'its', 'their') + ' Wh rating');
        }
        body += buts.length
          ? 'For the rest, ' + list(buts) + '.'
          : 'Every size works, so choose on how many you want to charge at once.';
      } else if (noRoom.length && !blocked.length) {
        tone = 'warn';
        head = 'One fits. ' + qty + ' does not.';
        body = 'A single battery goes into the ' + names(noRoom) + ' happily, but there is not ' +
          'room for ' + qty + ' at once. The ' + noRoom[noRoom.length - 1].box.short +
          ' holds about ' + noRoom[noRoom.length - 1].roomFor +
          '. Drop the number, or charge them in batches.';
      } else if (blocked.length) {
        tone = 'warn';
        head = 'The battery fits. The charge plug is what stops it.';
        body = size + (many ? ' drop' : ' drops') + ' into the ' + names(blocked) +
          ', but once the charger is plugged in it needs more room than there is. ' +
          'Try marking the port on a different face, or check whether your charge lead ' +
          'comes off at a right angle, which takes up far less space.';
      } else if (over.length) {
        tone = 'warn';
        head = 'It fits, but the capacity is too high.';
        body = size + (many ? ' go' : ' goes') + ' into the ' + names(over) +
          ', but that is past ' + verb(over, 'its', 'their') + ' Wh rating, so we would not ' +
          'charge it in there. Email us and we will talk you through the options.';
      } else {
        tone = 'no';
        head = 'None of the four will take it.';
        body = 'At ' + spec.replace(' each', '') + ' your battery is bigger than the ' + biggest.short +
          ', which is the largest we stock at ' + num(biggest.w) + ' x ' + num(biggest.d) +
          ' x ' + num(biggest.h) + 'mm inside. Check your measurements, then email us ' +
          'and we will tell you what else we can do.';
      }

      if (plugOn && tone === 'yes') {
        body += ' That allows for the charge plug you marked.';
      }

      return '' +
        '<div class="bfit-verdict__inner bfit-verdict--' + tone + '">' +
          '<p class="bfit-verdict__head">' + head + '</p>' +
          '<p class="bfit-verdict__body">' + body + '</p>' +
        '</div>';
    }

    function render() {
      var batt = readDims();
      var cap = readWh();
      var wh = cap.wh;
      var qty = readQty();

      // A listed mix of packs replaces the single-battery figures: its total is
      // the capacity, and the pack count is the quantity.
      var mix = refreshMix();
      var mixOn = mix.wh > 0;
      if (mixOn) {
        wh = mix.wh / mix.packs;
        qty = mix.packs;
        cap = { wh: wh, how: 'from the ' + mix.packs + ' packs you listed' };
      }
      var pLen = plugLen();
      var pAxis = pLen > 0 ? state.plugAxis : null;
      var solid = batt ? withPlug(batt, pAxis, pLen) : null;

      if (els.whOut) {
        els.whOut.hidden = !wh;
        if (els.whValue) {
          els.whValue.textContent = wh ? num(wh * qty) + 'Wh' : '';
        }
        if (els.whHow) {
          // For a mix the per-pack average is meaningless, so only show the
          // "N x M Wh each" form when the packs really are all the same.
          els.whHow.textContent = !wh
            ? ''
            : mixOn
              ? cap.how
              : qty > 1
                ? qty + ' x ' + num(wh) + 'Wh each (' + cap.how + ')'
                : cap.how || '';
        }
      }
      if (els.whHint) {
        els.whHint.hidden = !!wh || !cap.missing;
        els.whHint.textContent = cap.missing || '';
      }

      var ctx = { batt: batt, plugAxis: pAxis, plugLen: pLen };

      if (els.preview) {
        drawBattery(els.preview, ctx, { pickable: state.portOn });
        if (els.previewNote) {
          els.previewNote.textContent = batt
            ? num(batt[0]) + ' x ' + num(batt[1]) + ' x ' + num(batt[2]) + 'mm' +
              (pAxis !== null ? ', plus ' + num(pLen) + 'mm for the plug' : '')
            : 'Your battery, redrawn as you type';
        }
      }

      if (els.portHint) {
        els.portHint.textContent = !batt
          ? 'Add your measurements first, then mark the face the port is on.'
          : state.plugAxis === null
            ? 'Click the face of the battery the port is on.'
            : 'Set to ' + ['the end', 'a long side', 'the top'][state.plugAxis] +
              '. Click another face on the diagram to move it.';
      }
      if (els.portClear) els.portClear.hidden = state.plugAxis === null;

      var results = evaluate(batt, solid, wh, qty, mixOn);

      if (els.headroom) {
        els.headroom.hidden = !results.some(function (r) { return r.state === 'over'; });
      }

      if (els.verdict) {
        els.verdict.innerHTML = verdictMarkup(results, batt, wh, pAxis !== null, qty, mixOn);
      }

      if (els.miniNote) {
        // The Mini has no grommet, so it can never be charged in. Only worth
        // saying when the Mini is actually on the table.
        var miniFits = results.some(function (r) {
          return /mini/i.test(r.box.short + ' ' + r.box.name) &&
            (r.state === 'fits' || r.state === 'over' || r.state === 'no-room');
        });
        els.miniNote.hidden = !miniFits;
      }

      refreshShare(batt !== null);

      if (els.cards) {
        els.cards.innerHTML = results.map(function (r) {
          return cardMarkup(r, qty);
        }).join('');
        var svgs = els.cards.querySelectorAll('.bfit-card__svg');
        results.forEach(function (r, i) {
          if (svgs[i]) drawFit(svgs[i], r.box, ctx, r.state);
        });
      }
    }

    /* --- a mixed fleet of packs ----------------------------------------- */

    /**
     * An RC pilot does not have ten of the same pack, they have a drawer of
     * different sizes. Packs go into the box one at a time, so what decides it
     * is the total watt-hours, not how many of one thing tessellate. This lets
     * them add the sizes up; the dimensions in step 2 are then read as their
     * biggest pack.
     */
    function mixRowMarkup(i, row) {
      return '' +
        '<div class="bfit-mix__row" data-bfit-mix-row="' + i + '">' +
          '<label class="bfit-mix__cell">' +
            '<span>Qty</span>' +
            '<input type="number" inputmode="numeric" min="1" step="1" value="' +
              (row.qty || '') + '" data-bfit-mix-field="qty" aria-label="How many of this size">' +
          '</label>' +
          '<label class="bfit-mix__cell">' +
            '<span>Cells</span>' +
            '<input type="number" inputmode="numeric" min="1" step="1" value="' +
              (row.cells || '') + '" placeholder="6" data-bfit-mix-field="cells" aria-label="Cell count">' +
          '</label>' +
          '<label class="bfit-mix__cell">' +
            '<span>mAh</span>' +
            '<input type="number" inputmode="decimal" min="0" step="any" value="' +
              (row.mah || '') + '" placeholder="5000" data-bfit-mix-field="mah" aria-label="Capacity in mAh">' +
          '</label>' +
          '<span class="bfit-mix__wh" data-bfit-mix-wh></span>' +
          '<button type="button" class="bfit-mix__remove" data-bfit-mix-remove="' + i +
            '" aria-label="Remove this size">&times;</button>' +
        '</div>';
    }

    /** Watt-hours for one row, or 0 if it is not filled in yet. */
    function rowWh(row) {
      var qty = parseInt(row.qty, 10);
      var cells = parseFloat(row.cells);
      var mah = parseFloat(row.mah);
      if (!(qty > 0) || !(cells > 0) || !(mah > 0)) return 0;
      var chem = els.chem ? els.chem.value : 'liion';
      return qty * cells * (CELL_NOMINAL[chem] || 3.7) * (mah / 1000);
    }

    function mixTotals() {
      var wh = 0;
      var packs = 0;
      state.mix.forEach(function (row) {
        var w = rowWh(row);
        if (w > 0) {
          wh += w;
          packs += parseInt(row.qty, 10);
        }
      });
      return { wh: wh, packs: packs };
    }

    function drawMixRows() {
      if (!els.mixRows) return;
      els.mixRows.innerHTML = state.mix.map(function (row, i) {
        return mixRowMarkup(i, row);
      }).join('');
      // Per-row watt-hours, so it is obvious which line is the big one.
      state.mix.forEach(function (row, i) {
        var node = els.mixRows.querySelector('[data-bfit-mix-row="' + i + '"] [data-bfit-mix-wh]');
        var w = rowWh(row);
        if (node) node.textContent = w > 0 ? num(w) + 'Wh' : '';
      });
    }

    function refreshMix() {
      var t = mixTotals();
      var on = t.wh > 0;

      if (els.mixTotal) {
        els.mixTotal.hidden = !on;
        els.mixTotal.innerHTML = on
          ? '<strong>' + t.packs + ' packs, ' + num(t.wh) + 'Wh all up.</strong> ' +
            'That total is what we check against each box.'
          : '';
      }
      // The single-battery quantity stepper would double-count once a mix is
      // in play, so hide it rather than have two answers on screen.
      if (els.qtyRow) els.qtyRow.hidden = on;
      return t;
    }

    if (els.mixToggle) {
      els.mixToggle.addEventListener('click', function () {
        var open = els.mixPanel && els.mixPanel.hidden;
        if (els.mixPanel) els.mixPanel.hidden = !open;
        els.mixToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open && !state.mix.length) {
          state.mix.push({ qty: 1, cells: '', mah: '' });
          drawMixRows();
        }
        schedule();
      });
    }

    if (els.mixAdd) {
      els.mixAdd.addEventListener('click', function () {
        state.mix.push({ qty: 1, cells: '', mah: '' });
        drawMixRows();
        schedule();
      });
    }

    if (els.mixRows) {
      els.mixRows.addEventListener('input', function (e) {
        var field = e.target.getAttribute('data-bfit-mix-field');
        if (!field) return;
        var rowEl = e.target.closest('[data-bfit-mix-row]');
        if (!rowEl) return;
        var i = parseInt(rowEl.getAttribute('data-bfit-mix-row'), 10);
        if (!state.mix[i]) return;
        state.mix[i][field] = e.target.value;

        var whNode = rowEl.querySelector('[data-bfit-mix-wh]');
        var w = rowWh(state.mix[i]);
        if (whNode) whNode.textContent = w > 0 ? num(w) + 'Wh' : '';
        schedule();
      });

      els.mixRows.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-bfit-mix-remove]');
        if (!btn) return;
        state.mix.splice(parseInt(btn.getAttribute('data-bfit-mix-remove'), 10), 1);
        drawMixRows();
        schedule();
      });
    }

    /* --- getting power into the box ------------------------------------ */

    /**
     * The round AU three-pin plug does not fit through the fire collar
     * grommet. That single fact is behind a good share of "it fits but I
     * cannot use it" tickets, so the advice is keyed to what the customer's
     * charger actually looks like rather than to the battery.
     */
    var CHARGER_ADVICE = {
      iec: {
        head: 'You can charge inside the box.',
        body: 'Unplug the kettle lead from your charger and feed the IEC end through the ' +
          'grommet instead of the plug. The AU plug stays outside, the charger sits on the ' +
          'lid, and the latch still shuts. This is the usual setup for e-bike, scooter and ' +
          'most tool chargers.'
      },
      moulded: {
        head: 'Charge outside the box, store inside it.',
        body: 'If the mains lead is moulded into the charger as one piece, there is no way to ' +
          'get it through the grommet without cutting it, and you should not do that. Charge ' +
          'on a hard non-flammable surface with the box beside you, then put the battery in ' +
          'the Bat-Safe to store and carry. That is still most of the protection, because a ' +
          'pack sitting at rest is what usually lets go.'
      },
      lipo: {
        head: 'Run the leads out, keep the charger out.',
        body: 'Most LiPo chargers use the small figure-eight mains plug, which passes through ' +
          'the grommet on its own. Better again, leave the charger outside on the magnetic ' +
          'stand and run only the battery leads in: an XT60 or XT90 extension for the power ' +
          'side and a balance lead extension for the balance plug.'
      }
    };

    var accessories = [];
    var accTag = root.querySelector('[data-bfit-accessory-data]');
    if (accTag) {
      try {
        accessories = JSON.parse(accTag.textContent) || [];
      } catch (e) {
        accessories = [];
      }
    }

    function renderCharger() {
      var mode = state.charger;
      var advice = CHARGER_ADVICE[mode];

      if (els.chargerAdvice && advice) {
        els.chargerAdvice.innerHTML =
          '<p class="bfit-power__head">' + advice.head + '</p>' +
          '<p class="bfit-power__body">' + advice.body + '</p>';
      }

      if (els.accessories) {
        var wanted = accessories.filter(function (a) {
          var roles = (a.roles || 'all').split(/\s+/);
          return roles.indexOf('all') !== -1 || roles.indexOf(mode) !== -1;
        });

        els.accessories.innerHTML = wanted.map(function (a) {
          return '' +
            '<a class="bfit-acc" href="' + esc(a.url) + '">' +
              '<span class="bfit-acc__name">' + esc(a.name) + '</span>' +
              '<span class="bfit-acc__note">' + esc(a.note || '') + '</span>' +
              '<span class="bfit-acc__foot">' +
                '<span class="bfit-acc__price">' + esc(a.price || '') + '</span>' +
                (a.inStock === false
                  ? '<span class="bfit-acc__stock">Out of stock</span>'
                  : '') +
              '</span>' +
            '</a>';
        }).join('');
      }
    }

    els.chargerTabs.forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.charger = btn.getAttribute('data-bfit-charger');
        els.chargerTabs.forEach(function (b) {
          var on = b.getAttribute('data-bfit-charger') === state.charger;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        renderCharger();
      });
    });

    /* --- share and prefill --------------------------------------------- */

    /** Build a link that reopens this page with the same numbers filled in. */
    function shareUrl() {
      var base = window.location.origin + window.location.pathname;
      var parts = [];

      if (state.type) parts.push('t=' + state.type);
      parts.push('u=' + state.unit);
      if (state.portOn && state.plugAxis !== null) parts.push('p=' + state.plugAxis);
      if (els.chem && els.chem.value !== 'liion') parts.push('c=' + els.chem.value);

      PARAMS.forEach(function (pair) {
        var node = root.querySelector(pair[1]);
        if (!node || node.value === '') return;
        // The quantity and plug allowance default to something, so only carry
        // them when they were actually changed.
        if (pair[0] === 'q' && String(node.value) === '1') return;
        if (pair[0] === 'pl' && state.plugAxis === null) return;
        parts.push(pair[0] + '=' + encodeURIComponent(node.value));
      });

      return parts.length ? base + '?' + parts.join('&') : base;
    }

    /** Fill the form in from the query string, if there is one. */
    function applyParams() {
      var qs;
      try {
        qs = new URLSearchParams(window.location.search);
      } catch (e) {
        return;
      }
      if (!qs.toString()) return;

      // Type first: it seeds the chemistry and plug allowance, which anything
      // explicit in the link should then override.
      var t = qs.get('t');
      if (t && TYPES[t]) setType(t);

      var u = qs.get('u');
      if ((u === 'cm' || u === 'mm') && u !== state.unit) {
        var unitBtn = root.querySelector('[data-bfit-unit="' + u + '"]');
        if (unitBtn) unitBtn.click();
      }

      PARAMS.forEach(function (pair) {
        var v = qs.get(pair[0]);
        if (v === null || v === '') return;
        var node = root.querySelector(pair[1]);
        if (node) node.value = v;
      });

      var c = qs.get('c');
      if (c && els.chem && CELL_NOMINAL[c]) els.chem.value = c;

      var p = qs.get('p');
      if (p !== null && /^[012]$/.test(p)) {
        setPortOn(true);
        state.plugAxis = parseInt(p, 10);
      }
    }

    function refreshShare(hasBattery) {
      if (els.share) els.share.hidden = !hasBattery;
    }

    if (els.copy) {
      els.copy.addEventListener('click', function () {
        var url = shareUrl();
        var done = function () {
          var was = els.copy.textContent;
          els.copy.textContent = 'Link copied';
          els.copy.classList.add('is-done');
          setTimeout(function () {
            els.copy.textContent = was;
            els.copy.classList.remove('is-done');
          }, 2000);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(done, function () {
            window.prompt('Copy this link', url);
          });
        } else {
          window.prompt('Copy this link', url);
        }
      });
    }

    /* --- wiring -------------------------------------------------------- */

    var frame = null;
    function schedule() {
      // rAF is paused in a hidden tab (and in the theme editor's preview frame
      // while it is offscreen), so render straight away there.
      if (document.hidden) {
        render();
        return;
      }
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(render);
    }

    function setType(id) {
      var t = TYPES[id];
      if (!t) return;
      state.type = id;
      setPortOn(!!t.autoPort);

      els.typeBtns.forEach(function (b) {
        var on = b.getAttribute('data-bfit-type') === id;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      if (els.chem) els.chem.value = t.chem;
      // TYPES stores the plug allowance in millimetres, so convert it into
      // whichever unit the customer is working in.
      if (els.plugInput && t.plug) {
        els.plugInput.value = state.unit === 'cm' ? t.plug / 10 : t.plug;
      }
      if (els.portStep) els.portStep.hidden = !t.port;

      // An FPV pack means an RC charger, so open on that advice.
      var wantCharger = id === 'fpv' ? 'lipo' : 'iec';
      var tab = root.querySelector('[data-bfit-charger="' + wantCharger + '"]');
      if (tab && state.charger !== wantCharger) tab.click();

      root.classList.toggle('bfit--has-type', true);
      schedule();
    }

    root.addEventListener('input', function (e) {
      if (e.target.closest('[data-bfit-dim], [data-bfit-cap], [data-bfit-plug], [data-bfit-qty]')) schedule();
    });
    root.addEventListener('change', function (e) {
      if (e.target.closest('[data-bfit-chem]')) schedule();
    });

    /**
     * The plug allowance is off until asked for. Most people do not need it,
     * and a step that opens full of controls reads as more work than it is.
     */
    function setPortOn(on) {
      state.portOn = !!on;
      if (els.portOn) els.portOn.checked = state.portOn;
      if (els.portBody) els.portBody.hidden = !state.portOn;
      if (els.portSwitchText) els.portSwitchText.textContent = state.portOn ? 'On' : 'Off';

      if (state.portOn) {
        // Assume a face straight away. Waiting for a click on one of three
        // small targets meant switching this on appeared to do nothing.
        if (state.plugAxis === null) state.plugAxis = DEFAULT_PORT_AXIS;
      } else {
        state.plugAxis = null;
      }
      schedule();
    }

    if (els.portOn) {
      els.portOn.addEventListener('change', function () {
        setPortOn(els.portOn.checked);
      });
    }

    els.typeBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        setType(btn.getAttribute('data-bfit-type'));
      });
    });

    // Marking the charging port, by clicking a face of the battery.
    function pickFace(node) {
      var axis = parseInt(node.getAttribute('data-bfit-face'), 10);
      if (isNaN(axis)) return;
      state.plugAxis = state.plugAxis === axis ? null : axis;
      schedule();
    }

    if (els.preview) {
      els.preview.addEventListener('click', function (e) {
        var hit = e.target.closest('[data-bfit-face]');
        if (hit) pickFace(hit);
      });
      els.preview.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        var hit = e.target.closest && e.target.closest('[data-bfit-face]');
        if (hit) {
          e.preventDefault();
          pickFace(hit);
        }
      });
    }

    if (els.portClear) {
      els.portClear.addEventListener('click', function () {
        state.plugAxis = null;
        schedule();
      });
    }

    els.unit.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var next = btn.getAttribute('data-bfit-unit');
        if (next === state.unit) return;

        // Convert whatever is already typed, so the numbers stay meaningful.
        var k = next === 'cm' ? 0.1 : 10;
        var convert = function (i) {
          var v = parseFloat(i.value);
          if (isFinite(v)) i.value = Math.round(v * k * 100) / 100;
        };
        els.dim.forEach(convert);
        if (els.plugInput) convert(els.plugInput);

        state.unit = next;
        els.unit.forEach(function (b) {
          var on = b.getAttribute('data-bfit-unit') === next;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        els.unitLabels.forEach(function (l) { l.textContent = next; });
        schedule();
      });
    });

    els.qtySteps.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (!els.qty) return;
        var step = parseInt(btn.getAttribute('data-bfit-qty-step'), 10) || 0;
        els.qty.value = Math.max(1, Math.min(99, readQty() + step));
        schedule();
      });
    });

    var reset = root.querySelector('[data-bfit-reset]');
    if (reset) {
      reset.addEventListener('click', function () {
        els.dim.forEach(function (i) { i.value = i.getAttribute('value') || ''; });
        els.cap.forEach(function (i) { i.value = ''; });
        if (els.qty) els.qty.value = 1;
        state.plugAxis = null;

        // The mix drives capacity when it has rows, so leaving it behind meant
        // "start again" quietly kept using the old fleet.
        state.mix = [];
        drawMixRows();
        if (els.mixPanel) els.mixPanel.hidden = true;
        if (els.mixToggle) els.mixToggle.setAttribute('aria-expanded', 'false');

        setPortOn(false);
        schedule();
      });
    }

    applyParams();
    renderCharger();
    render();
  }

  function boot(scope) {
    (scope || document).querySelectorAll('[data-battery-fit]').forEach(function (n) {
      init(n);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  // The theme editor swaps section markup in place.
  document.addEventListener('shopify:section:load', function (e) {
    boot(e.target);
  });
})();
